/**
 * @component SchulungenTab — MA-Detail Tab "Schulungen"
 * @api GET /api/mitarbeiter/:id/schulungen
 * @i18n qmTest.*
 */
import { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Award, CheckCircle2, AlertCircle, Clock, Trophy, XCircle, Download, GraduationCap } from 'lucide-react';
import EmptyState from '../EmptyState';

const STATUS_META = {
  offen:           { color: '#338edc', bg: '#EFF6FF',  labelDef: 'Offen',           Icon: Clock },
  tour_offen:      { color: '#338edc', bg: '#EFF6FF',  labelDef: 'Tour offen',      Icon: Clock },
  test_offen:      { color: '#F59E0B', bg: '#FFFBEB',  labelDef: 'Test offen',      Icon: AlertCircle },
  bestanden:       { color: '#10B981', bg: '#ECFDF5',  labelDef: 'Bestanden',       Icon: Trophy },
  nicht_bestanden: { color: '#EF4444', bg: '#FEF2F2',  labelDef: 'Nicht bestanden', Icon: XCircle },
  abgelaufen:      { color: '#6B7280', bg: '#F3F4F6',  labelDef: 'Abgelaufen',      Icon: AlertCircle },
};

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function SchulungenTab({ maId }) {
  const { t } = useLanguage();
  const [data, setData] = useState({ zuweisungen: [], zertifikate: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!maId) return;
    fetch(`/api/mitarbeiter/${maId}/schulungen`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { zuweisungen: [], zertifikate: [] })
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [maId]);

  if (loading) {
    return (
      <div className="text-center py-8 text-sm" style={{ color: 'var(--text-tertiary,#6B7280)' }}>
        {t('qmTest.loading') || 'Lade …'}
      </div>
    );
  }

  const { zuweisungen = [], zertifikate = [] } = data;

  // Empty-State: keine Zuweisungen UND keine Zertifikate → ein einziger ganzheitlicher EmptyState
  const isCompletelyEmpty = zuweisungen.length === 0 && zertifikate.length === 0;

  if (isCompletelyEmpty) {
    return (
      <div className="bg-surface-card border border-line rounded-card">
        <EmptyState
          icon={<GraduationCap className="w-6 h-6" strokeWidth={1.5} />}
          title={t('qmTest.tab.emptyTitle') || 'Noch keine Schulungen'}
          description={t('qmTest.tab.emptyDescription') || 'Diese/r MA hat noch keine Test-Zuweisungen oder Zertifikate. Über das QM-Modul können Tests zugewiesen werden.'}
          ctaLabel={t('qmTest.tab.emptyCta') || 'Zum QM-Modul'}
          ctaHref="/qm"
        />
      </div>
    );
  }

  // KPI-Werte berechnen — Cards nur wenn mind. ein Wert > 0
  const kpis = [
    { key: 'zuweisungen', value: zuweisungen.length, label: t('qmTest.tab.zugewiesen') || 'Zugewiesen', color: '#338edc' },
    { key: 'offen', value: zuweisungen.filter(z => ['offen','tour_offen','test_offen'].includes(z.status)).length, label: t('qmTest.tab.offen') || 'Offen', color: '#F59E0B' },
    { key: 'bestanden', value: zuweisungen.filter(z => z.status === 'bestanden').length, label: t('qmTest.tab.bestanden') || 'Bestanden', color: '#10B981' },
    { key: 'zertifikate', value: zertifikate.filter(z => !z.gueltig_bis || new Date(z.gueltig_bis) > new Date()).length, label: t('qmTest.tab.zertifikate') || 'Zertifikate', color: '#6366F1' },
  ];
  const allKpisZero = kpis.every(k => k.value === 0);

  return (
    <div className="space-y-5">

      {/* KPIs — nur sichtbar wenn mind. ein Wert > 0 */}
      {!allKpisZero && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpis.map(k => (
            <div key={k.key} className="bg-surface-card border border-line rounded-card p-4">
              <p className="text-xs" style={{ color: 'var(--text-tertiary,#6B7280)' }}>{k.label}</p>
              <p className="text-2xl font-bold mt-1" style={{ color: 'var(--text-primary,#1A1A2E)' }}>{k.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Zuweisungen */}
      <div className="bg-surface-card border border-line rounded-card overflow-hidden">
        <div className="px-4 py-3 border-b border-line flex items-center gap-2">
          <Award className="w-4 h-4" style={{ color: '#338edc' }} strokeWidth={1.5} />
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary,#1A1A2E)' }}>
            {t('qmTest.tab.zuweisungenTitle') || 'Zugewiesene Tests'}
          </h3>
        </div>
        {zuweisungen.length === 0 ? (
          <EmptyState
            variant="compact"
            icon={<Award className="w-5 h-5" strokeWidth={1.5} />}
            title={t('qmTest.tab.keineZuweisungen') || 'Keine Test-Zuweisungen'}
            description={t('qmTest.tab.keineZuweisungenHint') || 'Tests werden über das QM-Modul zugewiesen.'}
          />
        ) : (
          <div className="divide-y divide-[var(--border-default,#E5E7EB)]">
            {zuweisungen.map(z => {
              const meta = STATUS_META[z.status] || STATUS_META.offen;
              const Icon = meta.Icon;
              return (
                <div key={z.id} className="px-4 py-3 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: meta.bg }}>
                    <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color: meta.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary,#1A1A2E)' }}>
                      {z.test_titel || '—'}
                    </p>
                    <p className="text-xs truncate" style={{ color: 'var(--text-tertiary,#6B7280)' }}>
                      {z.prozess_titel}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5 text-[11px] flex-wrap">
                      <span className="px-2 py-0.5 rounded-full font-medium" style={{ background: meta.bg, color: meta.color }}>
                        {meta.labelDef}
                      </span>
                      <span style={{ color: 'var(--text-tertiary,#9CA3AF)' }}>
                        {t('qmTest.tab.zugewiesen_am') || 'zugewiesen'}: {fmt(z.zugewiesen_am)}
                      </span>
                      {z.faellig_bis && (
                        <span style={{ color: 'var(--text-tertiary,#9CA3AF)' }}>
                          · {t('qmTest.tab.faellig') || 'fällig'}: {fmt(z.faellig_bis)}
                        </span>
                      )}
                      {z.versuche_genutzt > 0 && (
                        <span style={{ color: 'var(--text-tertiary,#9CA3AF)' }}>
                          · {z.versuche_genutzt} {t('qmTest.tab.versuche') || 'Versuche'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Zertifikate */}
      <div className="bg-surface-card border border-line rounded-card overflow-hidden">
        <div className="px-4 py-3 border-b border-line flex items-center gap-2">
          <Trophy className="w-4 h-4" style={{ color: '#10B981' }} strokeWidth={1.5} />
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary,#1A1A2E)' }}>
            {t('qmTest.tab.zertifikateTitle') || 'Zertifikate'}
          </h3>
        </div>
        {zertifikate.length === 0 ? (
          <EmptyState
            variant="compact"
            icon={<Trophy className="w-5 h-5" strokeWidth={1.5} />}
            title={t('qmTest.tab.keineZertifikate') || 'Noch keine Zertifikate'}
            description={t('qmTest.tab.keineZertifikateHint') || 'Zertifikate werden automatisch nach erfolgreich bestandenen Tests ausgestellt.'}
          />
        ) : (
          <div className="divide-y divide-[var(--border-default,#E5E7EB)]">
            {zertifikate.map(z => {
              const abgelaufen = z.gueltig_bis && new Date(z.gueltig_bis) < new Date();
              return (
                <div key={z.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: abgelaufen ? '#F3F4F6' : '#ECFDF5' }}>
                    <Trophy className="w-4 h-4" strokeWidth={1.5} style={{ color: abgelaufen ? '#6B7280' : '#10B981' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary,#1A1A2E)' }}>
                      {z.test_titel || z.prozess_titel || '—'}
                    </p>
                    <div className="flex items-center gap-2 text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary,#6B7280)' }}>
                      <span>{z.score_pct}%</span>
                      <span>· {t('qmTest.tab.ausgestellt') || 'ausgestellt'}: {fmt(z.ausgestellt_am)}</span>
                      {z.gueltig_bis && (
                        <span className={abgelaufen ? 'text-rose-600' : ''}>
                          · {t('qmTest.tab.gueltig_bis') || 'gültig bis'}: {fmt(z.gueltig_bis)}
                        </span>
                      )}
                    </div>
                  </div>
                  <a
                    href={`/api/zertifikate/${z.id}/pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-line hover:bg-[var(--bg-inset,#F8F9FC)]"
                    style={{ color: 'var(--text-secondary,#4B5563)' }}
                    title={t('qmTest.tab.pdfDownload') || 'PDF herunterladen'}
                  >
                    <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
                    PDF
                  </a>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}
